import CancelIcon from "@mui/icons-material/Clear";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectUserSession } from "../slices/userSession/userSessionSlice";
import {
  ACTION_APPROVE,
  ACTION_CANCEL,
  ACTION_CANCEL_APPROVE,
  ACTION_CANCEL_REFUSE,
  ACTION_REFUSE,
  ACTION_REINSTATE,
  FREEFORM_REASON_CHAR_LIMIT,
  SELECT_HOURS_LIST,
  SELECT_MINUTES_LIST,
} from "../util/constants";
import { validateCannedReason, validateReasonComments, validateVerbalCheck } from "../util/inputValidation";
import {
  ARE_YOU_SURE_YOU_WANT_TO_APPROVE_MESSAGE,
  ARE_YOU_SURE_YOU_WANT_TO_CANCEL_MESSAGE,
  ARE_YOU_SURE_YOU_WANT_TO_REFUSE_MESSAGE,
  ARE_YOU_SURE_YOU_WANT_TO_REINSTATE_MESSAGE,
  DAILY_ALLOTMENT_IS_ALREADY_FULL,
  DUPLICATE_REQUEST,
  EARNED_AWARD_DAY_CREATED_WITH_SHIFT_TIMES,
  EMPLOYEE_HAS_BECOME_INELIGIBLE,
  EMPLOYEE_IS_ALREADY_OFF,
  EMPLOYEE_ON_OVERTIME_WILL_BE_OFFERED_RELEASE,
  END_TIME_IS_REQUIRED,
  FTO_DAT_BID_HAS_NOT_BEEN_AWARDED,
  HAVE_YOU_CONTACTED_THE_EMPLOYEE_VERBALLY,
  INCORRECT_TIMES_OR_SHIFT_LENGTH,
  INELIGIBLE_EMPLOYEE,
  INSUFFICIENT_TIME_OFF_BALANCES,
  OTHER,
  OVERLAPS_WITH_TRAINING_OR_OVERTIME,
  PARTIAL_SHIFT_MUST_BE_SUBMITTED_AS_4HOUR_VAC_HAT,
  PTO_BID_HAS_NOT_BEEN_AWARDED,
  SHIFT_HAS_BEEN_COVERED_BY_OVERTIME_OR_RELIEF,
  START_TIME_IS_REQUIRED,
  SUBMITTED_AFTER_CUTOFF_TIME,
  UNKNOWN_ACTION_MESSAGE,
  WILL_CREATE_MOT,
  WILL_CREATE_VOT,
  WOP_REQUEST_MUST_BE_AT_BEGINNING_OR_END,
  _4_HOUR_VAC_HAT_MUST_BE_AT_BEGINNING_OR_END_OF_SHIFT,
} from "../util/messageConstants";
import {
  TOR_validateShiftEndTime,
  validateAdminTORShiftStartSubmissionDeadline,
  validateAdminWOPDeadline,
  validateIfAdminAttemptsActionOnTheirOwnTOR,
} from "../util/timeOffRequestInputValidation";
import ConfirmButton from "./fundamentals/ConfirmButton";
import SlideUpTransition from "./fundamentals/SlideUpTransition";
import WarningDialog from "./fundamentals/WarningDialog";
import TimeOffRequestAdminActionConfirm from "./TimeOffRequestAdminActionConfirm";
import {
  getWOPRequestDateTimes,
  isLWOPTOWOPAllDayRequest,
  isLWOPTOWOPDayOfRequest,
  isLWOPTOWOPRequest,
  validateWOPPreferredLeaveTime,
} from "../util/timeOffRequestUtils";
import { makeStyles } from "@mui/styles";
import { getTORPreferredLeaveTime, getTORShift } from "../util/timeOffExportMapper";

const useStyles = makeStyles((theme) => ({
  messageBorderWOP: {
    width: "230px",
  },
  warningFormat: {
    textAlign: "center",
    whiteSpace: "pre-line",
    float: "left",
    marginLeft: "15px",
  },
  datetimeInputGrid: {
    maxWidth: "100vw",
    height: "max-content",
    textAlign: "left",
    // marginLeft: "1px",
  },
  dateInputBox: {
    display: "inline-block",
  },
  datetimeInputBox: {
    display: "inline-block",
  },
  datetimeInputBoxWOP: {
    display: "inline-block",
    width: "100%",
  },
  timeInputBox: {
    display: "inline-block",
  },
  timeInput: {
    width: "70px",
    maxWidth: "20vw",
  },
  timeInputDayOf: {
    width: "110px",
    maxWidth: "20vw",
  },
  timeColonBox: {
    display: "inline-block",
    fontSize: "20px",
    lineHeight: "0",
    top: "18px",
    position: "relative",
  },
  characterLimitLabel: {
    textAlign: "right",
    marginRight: "40px",
  },
  characterLimitLabelRed: {
    textAlign: "right",
    marginRight: "40px",
    color: "red"
  }
}));

const TimeOffRequestAdminActionCheckDialog = (props) => {
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isActionError, setIsActionError] = useState(false);
  const [actionErrors, setActionErrors] = useState("");
  const [cannedReasonTouched, setCannedReasonTouched] = useState(false);
  const [cannedReasonErrors, setCannedReasonErrors] = useState("");
  const [cannedReason, setCannedReason] = useState("");
  const [isReasonCommentsRequired, setIsReasonCommentsRequired] = useState(false);
  const [reasonCommentsTouched, setReasonCommentsTouched] = useState(false);
  const [reasonCommentsErrors, setReasonCommentsErrors] = useState("");
  const [reasonComments, setReasonComments] = useState("");
  const [verbalCheckErrors, setVerbalCheckErrors] = useState("");
  const [isVerbalCheckChecked, setIsVerbalCheckChecked] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [approvedWOPStartHour, setApprovedWOPStartHour] = useState("");
  const [approvedWOPStartMin, setApprovedWOPStartMin] = useState("");
  const [approvedWOPStartTimeError, setApprovedWOPStartTimeError] = useState("");
  const [approvedWOPEndHour, setApprovedWOPEndHour] = useState("");
  const [approvedWOPEndMin, setApprovedWOPEndMin] = useState("");
  const [approvedWOPEndTimeError, setApprovedWOPEndTimeError] = useState("");

  useEffect(() => {
    initialValidation();
    setIsReasonCommentsRequired(isReasonCommentsAlwaysRequired());

    if (isLWOPTOWOPAllDayRequest(props.timeOffRequest.timeOffRequestType)) {
      // set values as read only for ALL DAY WOP requests
      setApprovedWOPStartHour(props.timeOffRequest.shiftStart.hour);
      setApprovedWOPStartMin(props.timeOffRequest.shiftStart.minute);
      setApprovedWOPEndHour(props.timeOffRequest.shiftEnd.hour);
      setApprovedWOPEndMin(props.timeOffRequest.shiftEnd.minute);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialValidation = () => {
    let validationMessage = "";

    validationMessage = validateIfAdminAttemptsActionOnTheirOwnTOR(
      userSession.eid,
      props.timeOffRequest.empId,
      props.actionSelected
    );

    if (validationMessage === "") {
      if (isLWOPTOWOPRequest(props.timeOffRequest.timeOffRequestType)) {
        validationMessage = validateAdminWOPDeadline(
          props.timeOffRequest.timeOffRequestType,
          props.timeOffRequest.station,
          props.timeOffRequest.shiftStart,
          props.timeOffRequest.shiftEnd,
          props.actionSelected
        );
      } else
        validationMessage = validateAdminTORShiftStartSubmissionDeadline(
          props.timeOffRequest.shiftStart,
          props.actionSelected,
          props.timeOffRequest.station
        );
    }

    setWarningMessage(validationMessage);

    return validationMessage;
  };

  const isFormValidationSuccess = () => {
    let validationMessage = initialValidation();
    let verbalCheckErrors = "";
    let cannedReasonErrors = "";
    let reasonCommentsErrors = "";
    let approvedLeaveTimeError = false;

    if (isLWOPTOWOPRequest(props.timeOffRequest.timeOffRequestType) && props.actionSelected === ACTION_APPROVE)
      approvedLeaveTimeError = validateApprovedLeaveTimes();

    verbalCheckErrors = validateVerbalCheck(isVerbalCheckChecked, isVerbalCheckRequired());
    cannedReasonErrors = validateCannedReason(cannedReason, isCannedReasonRequired());
    reasonCommentsErrors = validateReasonComments(reasonComments, isReasonCommentsRequired);

    setWarningMessage(validationMessage);
    setVerbalCheckErrors(verbalCheckErrors);
    setCannedReasonTouched(true);
    setCannedReasonErrors(cannedReasonErrors);
    setReasonCommentsTouched(true);
    setReasonCommentsErrors(reasonCommentsErrors);

    return (
      validationMessage === "" &&
      verbalCheckErrors === "" &&
      cannedReasonErrors === "" &&
      reasonCommentsErrors === "" &&
      !approvedLeaveTimeError
    );
  };

  const validateApprovedLeaveTimes = () => {
    let isError = false;

    if (approvedWOPStartHour === "" || approvedWOPStartMin === "") {
      setApprovedWOPStartTimeError(START_TIME_IS_REQUIRED);
      isError = true;
    }
    if (approvedWOPEndHour === "" || approvedWOPEndMin === "") {
      setApprovedWOPEndTimeError(END_TIME_IS_REQUIRED);
      isError = true;
    }

    if (!isError) {
      const shiftStart = props.timeOffRequest.shiftStart;
      const shiftEnd = props.timeOffRequest.shiftEnd;
      const parsedApprovedWOPStartHour = parseInt(approvedWOPStartHour);
      const parsedApprovedWOPStartMin = parseInt(approvedWOPStartMin);
      const parsedApprovedWOPEndHour = parseInt(approvedWOPEndHour);
      const parsedApprovedWOPEndMin = parseInt(approvedWOPEndMin);

      let error = TOR_validateShiftEndTime(
        props.timeOffRequest.timeOffRequestType,
        shiftStart,
        shiftEnd,
        parsedApprovedWOPStartHour,
        parsedApprovedWOPStartMin,
        parsedApprovedWOPEndHour,
        parsedApprovedWOPEndMin,
        props.timeOffRequest.station
      );
      setApprovedWOPEndTimeError(error);
      isError = error !== "";

      if (!isError) {
        const approvedReleaseStart = {
          preferredWOPStartHour: parsedApprovedWOPStartHour,
          preferredWOPStartMin: parsedApprovedWOPStartMin,
        };
        const approvedReleaseEnd = {
          preferredWOPEndHour: parsedApprovedWOPEndHour,
          preferredWOPEndMin: parsedApprovedWOPEndMin,
        };
        error = validateWOPPreferredLeaveTime(
          shiftStart,
          shiftEnd,
          approvedReleaseStart,
          approvedReleaseEnd,
          false,
          props.timeOffRequest.station,
          true
        );
        setApprovedWOPStartTimeError(error);
        setApprovedWOPEndTimeError(error);
      }

      if (error !== "") {
        isError = true;
      }
    }

    return isError;
  };

  const getApprovedTimings = () => {
    const approvedDates = getWOPRequestDateTimes(
      props.timeOffRequest.shiftStart,
      props.timeOffRequest.shiftEnd,
      parseInt(approvedWOPStartHour),
      parseInt(approvedWOPStartMin),
      parseInt(approvedWOPEndHour),
      parseInt(approvedWOPEndMin),
      false
    );
    return {
      approvedWOPStart: approvedDates.requestStart.toISO(),
      approvedWOPEnd: approvedDates.requestEnd.toISO(),
    };
  };

  const isReasonCommentsAlwaysRequired = () => {
    return props.actionSelected === ACTION_REINSTATE && !isLWOPTOWOPRequest(props.timeOffRequest.timeOffRequestType);
  };

  const isVerbalCheckRequired = () => {
    return props.actionSelected === ACTION_CANCEL && !isLWOPTOWOPRequest(props.timeOffRequest.timeOffRequestType);
  };

  const isCannedReasonRequired = () => {
    if (isLWOPTOWOPRequest(props.timeOffRequest.timeOffRequestType)) return props.actionSelected === ACTION_REFUSE;
    return (
      props.actionSelected === ACTION_REFUSE ||
      props.actionSelected === ACTION_CANCEL_REFUSE ||
      props.actionSelected === ACTION_CANCEL
    );
  };

  const handleClickDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    clearDialogState();
    props.onClickDialogClose(event, reason);
  };

  const clearDialogState = () => {
    setIsActionLoading(false);
    setIsActionError(false);
    setActionErrors("");
  };

  const handleSetIsActionError = (isActionError) => {
    setIsActionError(isActionError);
  };

  const handleSetActionErrors = (actionErrors) => {
    setActionErrors(actionErrors);
  };

  const handleSetIsActionLoading = (isActionLoading) => {
    setIsActionLoading(isActionLoading);
  };

  const handleSetIsActionSuccess = (isActionSuccess) => {
    if (isActionSuccess && props.onTimeOffRequestDialogClose) {
      props.onTimeOffRequestDialogClose();
    }
  };

  const handleCannedReasonSelectChange = (event) => {
    setCannedReason(event.target.value);
    if (event.target.value === OTHER) {
      setIsReasonCommentsRequired(true);
    } else {
      setReasonCommentsErrors("");
      setIsReasonCommentsRequired(isReasonCommentsAlwaysRequired());
    }
  };

  const handleCannedReasonSelectBlur = (event) => {
    setCannedReasonErrors(validateCannedReason(event.target.value));
  };

  const handleReasonCommentsChange = (event) => {
    setReasonComments(event.target.value);
    setReasonCommentsTouched(true);
  };

  const handleReasonCommentsBlur = (event) => {
    setReasonCommentsErrors(validateReasonComments(event.target.value, isReasonCommentsRequired));
    setReasonCommentsTouched(true);
  };

  const handleCommentsClear = (event) => {
    setReasonComments("");
    setReasonCommentsErrors(validateReasonComments("", isReasonCommentsRequired));
    setReasonCommentsTouched(true);
  };

  const handleIsVerbalCheckChecked = (event) => {
    if (event.target.checked) {
      setVerbalCheckErrors("");
    }
    setIsVerbalCheckChecked(event.target.checked);
  };

  const handleApprovedWOPStartHourChange = (selectedStartHour) => {
    setApprovedWOPStartTimeError("");
    setApprovedWOPStartHour(selectedStartHour.target.value);

    if (approvedWOPStartMin !== "") {
      setApprovedWOPStartTimeError("");
    }

    if (approvedWOPEndHour !== "" && approvedWOPEndMin !== "") {
      setApprovedWOPEndTimeError("");
    }
  };

  const handleApprovedWOPStartMinChange = (selectedStartMin) => {
    setApprovedWOPStartTimeError("");
    setApprovedWOPStartMin(selectedStartMin.target.value);

    if (!approvedWOPStartHour && approvedWOPStartHour !== 0) {
      setApprovedWOPStartTimeError(START_TIME_IS_REQUIRED);
    }

    if (approvedWOPEndHour !== "" && approvedWOPEndMin !== "") {
      setApprovedWOPEndTimeError("");
    }
  };

  const handleApprovedWOPEndHourChange = (selectedEndHour) => {
    setApprovedWOPEndTimeError("");
    setApprovedWOPEndHour(selectedEndHour.target.value);

    if (approvedWOPEndMin !== "") {
      setApprovedWOPEndTimeError("");
    }

    if (approvedWOPStartHour !== "" && approvedWOPStartMin !== "") {
      setApprovedWOPStartTimeError("");
    }
  };

  const handleApprovedWOPEndMinChange = (selectedEndMin) => {
    setApprovedWOPEndTimeError("");
    setApprovedWOPEndMin(selectedEndMin.target.value);

    if (approvedWOPEndHour === "") {
      setApprovedWOPEndTimeError(END_TIME_IS_REQUIRED);
    }

    if (approvedWOPStartHour !== "" && approvedWOPStartMin !== "") {
      setApprovedWOPStartTimeError("");
    }
  };

  const renderReasonMenuItem = (reason) => {
    return (
      <MenuItem key={reason} value={reason} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
        {reason}
      </MenuItem>
    );
  };

  const renderRefuseDialog = () => {
    const CancelCommentsEndAdornment = () => {
      return (
        <IconButton size="small" className="cancel-icon-button" onMouseDown={handleCommentsClear}>
          <CancelIcon />
        </IconButton>
      );
    };

    const renderTORCannedRefusalReason = () => {
      return (
        <Grid item xs={12}>
            <Box mt={1} mb={1}>
              <FormControl variant="outlined">
                <InputLabel>Reason</InputLabel>
                <Select
                  id="refuseReasonSelect"
                  className="select-field"
                  
                  value={cannedReason}
                  onChange={handleCannedReasonSelectChange}
                  onBlur={handleCannedReasonSelectBlur}
                  label="Reason"
                  error={cannedReasonErrors !== ""}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxWidth: 200,
                      },
                    }
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>Reason Options</em>
                  </MenuItem>
                  {renderReasonMenuItem(_4_HOUR_VAC_HAT_MUST_BE_AT_BEGINNING_OR_END_OF_SHIFT)}
                  {renderReasonMenuItem(DAILY_ALLOTMENT_IS_ALREADY_FULL)}
                  {renderReasonMenuItem(DUPLICATE_REQUEST)}
                  {renderReasonMenuItem(EMPLOYEE_IS_ALREADY_OFF)}
                  {renderReasonMenuItem(FTO_DAT_BID_HAS_NOT_BEEN_AWARDED)}
                  {renderReasonMenuItem(INCORRECT_TIMES_OR_SHIFT_LENGTH)}
                  {renderReasonMenuItem(INELIGIBLE_EMPLOYEE)}
                  {renderReasonMenuItem(INSUFFICIENT_TIME_OFF_BALANCES)}
                  {renderReasonMenuItem(OVERLAPS_WITH_TRAINING_OR_OVERTIME)}
                  {renderReasonMenuItem(PARTIAL_SHIFT_MUST_BE_SUBMITTED_AS_4HOUR_VAC_HAT)}
                  {renderReasonMenuItem(PTO_BID_HAS_NOT_BEEN_AWARDED)}
                  {renderReasonMenuItem(SUBMITTED_AFTER_CUTOFF_TIME)}
                  {renderReasonMenuItem(WILL_CREATE_MOT)}
                  {renderReasonMenuItem(WILL_CREATE_VOT)}
                  {renderReasonMenuItem(OTHER)}
                </Select>
                {cannedReasonErrors !== "" && <FormHelperText error>{cannedReasonErrors}</FormHelperText>}
              </FormControl>
            </Box>
          </Grid>
      )
    }

    const renderWOPCannedRefusalReason = () => {
      return (
        <Grid item xs={12}>
            <Box mt={1} mb={1}>
              <FormControl variant="outlined">
                <InputLabel>Reason</InputLabel>
                <Select
                  id="refuseReasonSelect"
                  className="select-field"
                  value={cannedReason}
                  onChange={handleCannedReasonSelectChange}
                  onBlur={handleCannedReasonSelectBlur}
                  label="Reason"
                  error={cannedReasonErrors !== ""}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxWidth: 200,
                      },
                    }
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>Reason Options</em>
                  </MenuItem>
                  {renderReasonMenuItem(DUPLICATE_REQUEST)}
                  {renderReasonMenuItem(EMPLOYEE_IS_ALREADY_OFF)}
                  {renderReasonMenuItem(EMPLOYEE_ON_OVERTIME_WILL_BE_OFFERED_RELEASE)}
                  {renderReasonMenuItem(INCORRECT_TIMES_OR_SHIFT_LENGTH)}
                  {renderReasonMenuItem(INELIGIBLE_EMPLOYEE)}
                  {renderReasonMenuItem(WOP_REQUEST_MUST_BE_AT_BEGINNING_OR_END)}
                  {renderReasonMenuItem(OTHER)}
                </Select>
                {cannedReasonErrors !== "" && <FormHelperText error>{cannedReasonErrors}</FormHelperText>}
              </FormControl>
            </Box>
          </Grid>
      )
    }

    return (
      <Grid container>
        <Grid item xs={12} className="action-check-dialog-question">
          {ARE_YOU_SURE_YOU_WANT_TO_REFUSE_MESSAGE}
        </Grid>
        {!isLWOPTOWOPRequest(props.timeOffRequest.timeOffRequestType) && (
          <>{renderTORCannedRefusalReason()}</>
        )}
        {isLWOPTOWOPRequest(props.timeOffRequest.timeOffRequestType) && (
          <>{renderWOPCannedRefusalReason()}</>
        )}
        <Grid item xs={12}>
          <Box mt={1} mb={1}>
            <TextField
              className="text-field"
              label="Comments"
              variant="outlined"
              value={reasonComments}
              onChange={handleReasonCommentsChange}
              onBlur={handleReasonCommentsBlur}
              helperText={reasonCommentsErrors}
              InputProps={{
                endAdornment:
                  reasonComments !== "" ? (
                    <InputAdornment position="start">
                      <CancelCommentsEndAdornment />
                    </InputAdornment>
                  ) : (
                    ""
                  ),
              }}
              multiline
              error={reasonCommentsTouched && reasonCommentsErrors !== ""}
            />
            {reasonComments.length > FREEFORM_REASON_CHAR_LIMIT ? (
              <div className="character-limit-label-dialog character-limit-label-red">
                {reasonComments.length} / {FREEFORM_REASON_CHAR_LIMIT}
              </div>
            ) : (
              <div className="character-limit-label-dialog">
                {reasonComments.length} / {FREEFORM_REASON_CHAR_LIMIT}
              </div>
            )}
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderCancelRefuseDialog = () => {
    const CancelCommentsEndAdornment = () => {
      return (
        <IconButton size="small" className="cancel-icon-button" onMouseDown={handleCommentsClear}>
          <CancelIcon />
        </IconButton>
      );
    };
    return (
      <Grid container>
        <Grid item xs={12} className="action-check-dialog-question">
          {ARE_YOU_SURE_YOU_WANT_TO_REFUSE_MESSAGE}
        </Grid>
        <Grid item xs={12}>
          <Box mt={1} mb={1}>
            <FormControl variant="outlined">
              <InputLabel>Reason</InputLabel>
              <Select
                id="refuseReasonSelect"
                className="select-field"
                value={cannedReason}
                onChange={handleCannedReasonSelectChange}
                onBlur={handleCannedReasonSelectBlur}
                label="Reason"
                error={cannedReasonTouched && cannedReasonErrors !== ""}
              >
                <MenuItem value="" disabled>
                  <em>Reason Options</em>
                </MenuItem>
                {renderReasonMenuItem(SHIFT_HAS_BEEN_COVERED_BY_OVERTIME_OR_RELIEF)}
                {renderReasonMenuItem(SUBMITTED_AFTER_CUTOFF_TIME)}
                {renderReasonMenuItem(OTHER)}
              </Select>
              {cannedReasonTouched && cannedReasonErrors !== "" && (
                <FormHelperText error>{cannedReasonErrors}</FormHelperText>
              )}
            </FormControl>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box mt={1} mb={1}>
            <TextField
              className="text-field"
              label="Comments"
              variant="outlined"
              value={reasonComments}
              onChange={handleReasonCommentsChange}
              onBlur={handleReasonCommentsBlur}
              helperText={reasonCommentsErrors}
              InputProps={{
                endAdornment:
                  reasonComments !== "" ? (
                    <InputAdornment position="start">
                      <CancelCommentsEndAdornment />
                    </InputAdornment>
                  ) : (
                    ""
                  ),
              }}
              multiline
              error={reasonCommentsTouched && reasonCommentsErrors !== ""}
            />
            {reasonComments.length > FREEFORM_REASON_CHAR_LIMIT ? (
              <div className="character-limit-label-dialog character-limit-label-red">
                {reasonComments.length} / {FREEFORM_REASON_CHAR_LIMIT}
              </div>
            ) : (
              <div className="character-limit-label-dialog">
                {reasonComments.length} / {FREEFORM_REASON_CHAR_LIMIT}
              </div>
            )}
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderCancelDialog = () => {
    const CancelCommentsEndAdornment = () => {
      return (
        <IconButton size="small" className="cancel-icon-button" onMouseDown={handleCommentsClear}>
          <CancelIcon />
        </IconButton>
      );
    };
    return (
      <Grid container>
        <Grid item xs={12} className="action-check-dialog-question">
          {ARE_YOU_SURE_YOU_WANT_TO_CANCEL_MESSAGE}
        </Grid>
        {!isLWOPTOWOPRequest(props.timeOffRequest.timeOffRequestType) && (
          <Grid item xs={12}>
            <Box mt={1} mb={1}>
              <FormControl required component="fieldset" error={verbalCheckErrors !== ""} className="checkbox-field">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isVerbalCheckChecked}
                      onChange={handleIsVerbalCheckChecked}
                      name="verbalCheck"
                      color="primary"
                    />
                  }
                  label={HAVE_YOU_CONTACTED_THE_EMPLOYEE_VERBALLY}
                />
                {verbalCheckErrors !== "" && (
                  <FormHelperText className="checked-error" error>
                    {verbalCheckErrors}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>
          </Grid>
        )}
        {!isLWOPTOWOPRequest(props.timeOffRequest.timeOffRequestType) && (
          <Grid item xs={12}>
            <Box mt={1} mb={1}>
              <FormControl variant="outlined">
                <InputLabel>Reason</InputLabel>
                <Select
                  id="refuseReasonSelect"
                  className="select-field"
                  value={cannedReason}
                  onChange={handleCannedReasonSelectChange}
                  onBlur={handleCannedReasonSelectBlur}
                  label="Reason"
                  error={cannedReasonErrors !== ""}
                >
                  <MenuItem value="" disabled>
                    <em>Reason Options</em>
                  </MenuItem>
                  {renderReasonMenuItem(EARNED_AWARD_DAY_CREATED_WITH_SHIFT_TIMES)}
                  {renderReasonMenuItem(EMPLOYEE_HAS_BECOME_INELIGIBLE)}
                  {renderReasonMenuItem(INSUFFICIENT_TIME_OFF_BALANCES)}
                  {renderReasonMenuItem(OTHER)}
                </Select>
                {cannedReasonErrors !== "" && <FormHelperText error>{cannedReasonErrors}</FormHelperText>}
              </FormControl>
            </Box>
          </Grid>
        )}
        <Grid item xs={12}>
          <Box mt={1} mb={1}>
            <TextField
              className="text-field"
              label="Comments"
              variant="outlined"
              value={reasonComments}
              onChange={handleReasonCommentsChange}
              onBlur={handleReasonCommentsBlur}
              helperText={reasonCommentsErrors}
              InputProps={{
                endAdornment:
                  reasonComments !== "" ? (
                    <InputAdornment position="start">
                      <CancelCommentsEndAdornment />
                    </InputAdornment>
                  ) : (
                    ""
                  ),
              }}
              multiline
              error={reasonCommentsTouched && reasonCommentsErrors !== ""}
            />
            {reasonComments.length > FREEFORM_REASON_CHAR_LIMIT ? (
              <div className="character-limit-label-dialog character-limit-label-red">
                {reasonComments.length} / {FREEFORM_REASON_CHAR_LIMIT}
              </div>
            ) : (
              <div className="character-limit-label-dialog">
                {reasonComments.length} / {FREEFORM_REASON_CHAR_LIMIT}
              </div>
            )}
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderReinstateDialog = () => {
    const CancelCommentsEndAdornment = () => {
      return (
        <IconButton size="small" className="cancel-icon-button" onMouseDown={handleCommentsClear}>
          <CancelIcon />
        </IconButton>
      );
    };
    return (
      <Grid container>
        <Grid item xs={12} className="action-check-dialog-question">
          {ARE_YOU_SURE_YOU_WANT_TO_REINSTATE_MESSAGE}
        </Grid>
        <Grid item xs={12}>
          <Box mt={1} mb={1}>
            <TextField
              className="text-field"
              label="Comments"
              variant="outlined"
              value={reasonComments}
              onChange={handleReasonCommentsChange}
              onBlur={handleReasonCommentsBlur}
              helperText={reasonCommentsErrors}
              InputProps={{
                endAdornment:
                  reasonComments !== "" ? (
                    <InputAdornment position="start">
                      <CancelCommentsEndAdornment />
                    </InputAdornment>
                  ) : (
                    ""
                  ),
              }}
              multiline
              error={reasonCommentsTouched && reasonCommentsErrors !== ""}
            />
            {reasonComments.length > FREEFORM_REASON_CHAR_LIMIT ? (
              <div className={classes.characterLimitLabelRed}>
                {reasonComments.length} / {FREEFORM_REASON_CHAR_LIMIT}
              </div>
            ) : (
              <div className={classes.characterLimitLabel}>
                {reasonComments.length} / {FREEFORM_REASON_CHAR_LIMIT}
              </div>
            )}
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderApproveLeaveTimes = () => {
    const renderHours = () => {
      return SELECT_HOURS_LIST.map((hour) => {
        return (
          <MenuItem key={hour} value={parseInt(hour)}>
            {hour}
          </MenuItem>
        );
      });
    };

    const renderMinutes = () => {
      return SELECT_MINUTES_LIST.map((minute) => {
        return (
          <MenuItem key={minute} value={parseInt(minute)}>
            {minute}
          </MenuItem>
        );
      });
    };

    return (
      <Grid container xs={12} sx={{ justifyContent: "center" }}>
        <div style={{ width: "100%", textAlign: "left" }}>
          <FormLabel sx={{ fontWeight: "bold" }}>
            {props.timeOffRequest.empLastName}, {props.timeOffRequest.empFirstName} ({props.timeOffRequest.empId})
          </FormLabel>
        </div>
        <div style={{ width: "100%", textAlign: "left" }}>
          <FormLabel sx={{ fontWeight: "bold" }}>Shift: </FormLabel>
          <FormLabel>{getTORShift(props.timeOffRequest)}</FormLabel>
        </div>
        {isLWOPTOWOPDayOfRequest(props.timeOffRequest.timeOffRequestType) && (
          <div style={{ width: "100%", textAlign: "left" }}>
            <FormLabel sx={{ fontWeight: "bold" }}>Preferred Leave Time: </FormLabel>
            <FormLabel>{getTORPreferredLeaveTime(props.timeOffRequest)}</FormLabel>
          </div>
        )}
        <div style={{ width: "100%", textAlign: "left", marginTop: "10px" }}>
          <FormLabel sx={{ color: "black", fontWeight: "bold", fontSize: "1.1rem" }}>Approved Leave Time</FormLabel>
        </div>
        <Grid className={classes.datetimeInputGrid} item xs={12}>
          <Box className={classes.datetimeInputBoxWOP} style={{ marginRight: "0px" }} mt={1} mb={1} mr={1}>
            <Grid container className={classes.dateInputBox}>
              <Grid item xs={12} className={classes.messageBorderWOP}>
                <Box className={classes.timeInputBox}>
                  <FormControl variant="outlined">
                    <InputLabel id="approvedWOPStartHourSelectLabel" error={!!approvedWOPStartTimeError}>
                      Start Hr
                    </InputLabel>
                    <Select
                      labelId="approvedWOPStartHourSelectLabel"
                      id="approvedWOPStartHourSelectElement"
                      className={classes.timeInputDayOf}
                      value={approvedWOPStartHour}
                      onChange={handleApprovedWOPStartHourChange}
                      label="Start Hr"
                      error={!!approvedWOPStartTimeError}
                      disabled={isLWOPTOWOPAllDayRequest(props.timeOffRequest.timeOffRequestType)}
                    >
                      <MenuItem value="" disabled>
                        <em>Hour</em>
                      </MenuItem>
                      {renderHours()}
                    </Select>
                  </FormControl>
                </Box>
                <Box className={classes.timeColonBox}>:</Box>
                <Box className={classes.timeInputBox}>
                  <FormControl variant="outlined">
                    <InputLabel id="approvedWOPStartMinSelectLabel" error={!!approvedWOPStartTimeError}>
                      Start Min
                    </InputLabel>
                    <Select
                      labelId="approvedWOPStartMinSelectLabel"
                      id="approvedWOPStartMinSelectElement"
                      className={classes.timeInputDayOf}
                      value={approvedWOPStartMin}
                      onChange={handleApprovedWOPStartMinChange}
                      label="Start Min"
                      error={!!approvedWOPStartTimeError}
                      disabled={isLWOPTOWOPAllDayRequest(props.timeOffRequest.timeOffRequestType)}
                    >
                      <MenuItem value="" disabled>
                        <em>Minute</em>
                      </MenuItem>
                      {renderMinutes()}
                    </Select>
                  </FormControl>
                </Box>
                <FormHelperText className={classes.warningFormat} error>
                  {approvedWOPStartTimeError}
                </FormHelperText>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid className={classes.datetimeInputGrid} item xs={12}>
          <Box className={classes.datetimeInputBoxWOP} style={{ marginRight: "0px" }} mt={1} mb={1} mr={1}>
            <Grid container className={classes.dateInputBox}>
              <Grid item xs={12} className={classes.messageBorderWOP}>
                <Box className={classes.timeInputBox}>
                  <FormControl variant="outlined">
                    <InputLabel id="approvedWOPEndHourSelectLabel" error={!!approvedWOPEndTimeError}>
                      End Hr
                    </InputLabel>
                    <Select
                      labelId="approvedWOPEndHourSelectLabel"
                      id="approvedWOPEndHourSelectElement"
                      className={classes.timeInputDayOf}
                      value={approvedWOPEndHour}
                      onChange={handleApprovedWOPEndHourChange}
                      label="End Hr"
                      disabled={isLWOPTOWOPAllDayRequest(props.timeOffRequest.timeOffRequestType)}
                      error={!!approvedWOPEndTimeError}
                    >
                      <MenuItem value="" disabled>
                        <em>Hour</em>
                      </MenuItem>
                      {renderHours()}
                    </Select>
                  </FormControl>
                </Box>
                <Box className={classes.timeColonBox}>:</Box>
                <Box className={classes.timeInputBox}>
                  <FormControl variant="outlined">
                    <InputLabel id="approvedWOPEndMinuteSelectLabel" error={!!approvedWOPEndTimeError}>
                      End Min
                    </InputLabel>
                    <Select
                      labelId="approvedWOPEndMinuteSelectLabel"
                      id="approvedWOPEndMinuteSelectElement"
                      className={classes.timeInputDayOf}
                      value={approvedWOPEndMin}
                      onChange={handleApprovedWOPEndMinChange}
                      label="End Min"
                      disabled={isLWOPTOWOPAllDayRequest(props.timeOffRequest.timeOffRequestType)}
                      error={!!approvedWOPEndTimeError}
                    >
                      <MenuItem value="" disabled>
                        <em>Minute</em>
                      </MenuItem>
                      {renderMinutes()}
                    </Select>
                  </FormControl>
                </Box>
                <FormHelperText className={classes.warningFormat} error>
                  {approvedWOPEndTimeError}
                </FormHelperText>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderDialogDetails = () => {
    switch (props.actionSelected) {
      case ACTION_APPROVE:
        return isLWOPTOWOPRequest(props.timeOffRequest.timeOffRequestType)
          ? renderApproveLeaveTimes()
          : ARE_YOU_SURE_YOU_WANT_TO_APPROVE_MESSAGE;
      case ACTION_REFUSE:
        return renderRefuseDialog();
      case ACTION_CANCEL_APPROVE:
        return ARE_YOU_SURE_YOU_WANT_TO_APPROVE_MESSAGE;
      case ACTION_CANCEL_REFUSE:
        return renderCancelRefuseDialog();
      case ACTION_CANCEL:
        return renderCancelDialog();
      case ACTION_REINSTATE:
        return renderReinstateDialog();
      default:
        return UNKNOWN_ACTION_MESSAGE(props.actionSelected);
    }
  };

  return (
    <>
      {warningMessage !== "" ? (
        <WarningDialog
          isDialogOpen={props.isDialogOpen}
          handleClickDialogClose={handleClickDialogClose}
          header="Warning"
          message={warningMessage}
          severity={"error"}
        />
      ) : (
        <Dialog
          className="action-check-dialog"
          data-testid="action-check-dialog"
          fullWidth
          maxWidth="md"
          open={props.isDialogOpen}
          TransitionComponent={SlideUpTransition}
          keepMounted
          onClose={handleClickDialogClose}
          aria-labelledby="alert-dialog-slide-title"
          aria-describedby="alert-dialog-slide-description"
        >
          {/* <DialogTitle id="alert-dialog-slide-title" className="detail-dialog-title" style={{ textAlign: "left" }}> */}
          <DialogTitle id="alert-dialog-slide-title" sx={{ textAlign: "left" }}>
            Confirm
          </DialogTitle>
          {/* <DialogContent className="action-check-dialog-content"> */}
          <DialogContent sx={{ textAlign: "left" }}>
            <Grid container>
              <Grid item xs={12}>
                <DialogContentText component={"div"} id="alert-dialog-slide-description">
                  {renderDialogDetails()}
                  {isActionError ? <Alert severity="error">{actionErrors}</Alert> : ""}
                </DialogContentText>
              </Grid>
            </Grid>
          </DialogContent>
          {/* <DialogActions className="action-check-dialog-buttons"> */}
          <DialogActions  sx={{ justifyContent: "right" }}>
            <ConfirmButton
              variant="outlined"
              color="primary"
              onClick={handleClickDialogClose}
              isLoading={isActionLoading}
            >
              No
            </ConfirmButton>
            <TimeOffRequestAdminActionConfirm
              timeOffRequest={props.timeOffRequest}
              getApprovedTimings={getApprovedTimings}
              actionSelected={props.actionSelected}
              onClickDialogClose={handleClickDialogClose}
              onSetIsActionError={handleSetIsActionError}
              onSetActionErrors={handleSetActionErrors}
              onSetIsActionLoading={handleSetIsActionLoading}
              onSetIsActionSuccess={handleSetIsActionSuccess}
              isActionLoading={isActionLoading}
              cannedReason={cannedReason}
              reasonComments={reasonComments}
              isFormValidationSuccess={isFormValidationSuccess}
            />
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

TimeOffRequestAdminActionCheckDialog.propTypes = {
  timeOffRequest: PropTypes.object.isRequired,
  actionSelected: PropTypes.string.isRequired,
  isDialogOpen: PropTypes.bool.isRequired,
  onClickDialogClose: PropTypes.func.isRequired,
  onTimeOffRequestDialogClose: PropTypes.func.isRequired,
};

export default TimeOffRequestAdminActionCheckDialog;
